import { useState, useEffect } from 'react';
import { useGetUsers } from 'api/InternalAdminAPI';
import { selectEmail, viewAsUserRequest } from 'core/userContext';
import { closeNav } from 'modules/nav/ducks/actions';
import { formatUserOptions } from 'modules/nav/utils/users';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from 'ui/icons/action/Search';
import { WarningFill } from 'ui/icons/status/WarningFill';
import { FormField } from 'ui/formField';
import { BasicFilterSelect } from 'ui/selects/FilterSelect';
import { H4 } from 'ui/theme/typography/components';
import { BOSS_MANAGE_LINKS, BOSS_MONITOR_LINKS } from '../../constants/menus';
import { NavSlider } from '../NavSlider';
import { MenuOption } from './MenuOption';
import {
  GreyContainer,
  OptionsContainer,
  WarningContainer,
  SectionContainer,
  Heading,
  SectionHeader,
} from './styles';

export const TEXT = {
  HEADER: 'Boss Controls',
  IMPERSONATION_SELECT_LABEL: 'Impersonate a customer account',
  MANAGE_HEADING: 'Manage',
  MONITOR_HEADING: 'Monitor',
};

export const BossControlsMenu = () => {
  const dispatch = useDispatch();
  const email = useSelector(selectEmail);

  const onClose = () => {
    dispatch(closeNav());
  };

  const { data: users = [] } = useGetUsers();
  const [userOptions, setUserOptions] = useState<
    {
      id: string;
      label: string;
      value: string;
      doubleLine: string;
      searchtext: string;
    }[]
  >([]);

  useEffect(() => {
    const formattedUserOptions = formatUserOptions(users);
    setUserOptions(formattedUserOptions);
  }, [users.length]);

  const onSearchChange = (filterText?: string | undefined) => {
    /**
     * The local state is necessary only because the FilterSelect
     * currently doesn't natively support filtering by custom text,
     */
    const filteredUserOptions = formatUserOptions(users).filter((opt) =>
      opt.searchtext.toLowerCase().includes(filterText?.toLowerCase() || ''),
    );
    setUserOptions(filteredUserOptions);
  };

  const defaultValues = email ? [email] : undefined;

  const onSelect = (selected: string[]) => {
    const [selectedEmail] = selected;
    if (email !== selectedEmail) dispatch(viewAsUserRequest(selectedEmail));
  };

  return (
    <NavSlider onClose={onClose} headerContent={<H4>{TEXT.HEADER}</H4>}>
      <GreyContainer>
        <FormField
          helpText={
            <WarningContainer>
              <WarningFill isColorInherited />
              Actions performed while impersonating will affect the customer’s instance.
            </WarningContainer>
          }
          labelText={TEXT.IMPERSONATION_SELECT_LABEL}
        >
          <BasicFilterSelect
            Prefix={<Search isColorInherited />}
            inputContent={{
              filterPlaceholder: 'Search for a customer account...',
              triggerPlaceholder: 'Select a customer account...',
            }}
            emptyStateText="No matching accounts"
            options={userOptions}
            onSearchChange={onSearchChange}
            onSelect={onSelect}
            disableInternalFiltering
            defaultValues={defaultValues}
            isFullWidth
            onClick={(e) => {
              e.stopPropagation(); // Necessary to prevent the nav from closing
            }}
          />
        </FormField>
      </GreyContainer>
      <OptionsContainer>
        <SectionContainer>
          <SectionHeader>
            <Heading>{TEXT.MANAGE_HEADING}</Heading>
          </SectionHeader>
          {BOSS_MANAGE_LINKS.map((link) => (
            <MenuOption key={link.title} {...link} />
          ))}
        </SectionContainer>
        <SectionContainer>
          <SectionHeader>
            <Heading>{TEXT.MONITOR_HEADING}</Heading>
          </SectionHeader>
          {BOSS_MONITOR_LINKS.map((link) => (
            <MenuOption key={link.title} {...link} />
          ))}
        </SectionContainer>
      </OptionsContainer>
    </NavSlider>
  );
};
